import React, { useMemo } from 'react'
import { User } from 'api/typings'
import CollapsibleSection from '../Shared/CollapsibleSection'
import * as moment from 'moment-timezone'
import UserPenaltyWaive from './UserPenaltyWaive'
import UserStrike from './UserStrike'
import { dateTimeSubmittedFormat, getUserTimezone } from '../utils/general_util'
import { UserPastStrike } from './UserPastStrike'
import { Accordion, Heading, Message, Box, Text, Table } from '@workwhile/ui'
import { keyValueColumns } from 'components/Users/keyValueColumns'
import { DateOfBirth } from 'components/Users/DateOfBirth'
import { ProfilePicture } from 'components/Users/ProfilePicture'

interface Props {
  user: User
  refreshData: (e?: any) => void
}

const UserBasicInfo = ({ user, refreshData }: Props) => {
  const _timezone = getUserTimezone(user)

  const isActivePenalty =
    user.status === 'suspended' || user.status === 'blocked'
  const relevantPenalty = user.penalties
    ? user.penalties.filter((penalty) => penalty.penaltyType === user.status)[0]
    : null

  const getPaymentAccountString = (user) => {
    let accountString = ''
    for (let i = 0; i < user.paymentAccounts?.length || 0; i++) {
      const account = user.paymentAccounts[i]
      if (i > 0) {
        accountString += ', '
      }
      accountString += account.accountType
    }
    return accountString
  }

  const data = React.useMemo(
    () =>
      [
        {
          key: 'id',
          value: user.id,
        },
        {
          key: 'Status',
          value: user.status,
        },
        {
          key: isActivePenalty && relevantPenalty ? 'Penalty Policy' : null,
          value:
            isActivePenalty && relevantPenalty ? relevantPenalty.policy : null,
        },
        {
          key:
            isActivePenalty && relevantPenalty ? 'Penalty Triggered At' : null,
          value:
            isActivePenalty && relevantPenalty
              ? moment
                  .tz(relevantPenalty.createdAt, _timezone)
                  .format(dateTimeSubmittedFormat)
              : null,
        },
        {
          key:
            user.status === 'suspended' &&
            relevantPenalty &&
            relevantPenalty.policy === 'reject_late'
              ? 'Reject reason'
              : null,
          value:
            user.status === 'suspended' &&
            relevantPenalty &&
            relevantPenalty.policy === 'reject_late'
              ? relevantPenalty.offenses[0].reason
              : null,
        },
        {
          key:
            user.status === 'suspended' &&
            relevantPenalty &&
            relevantPenalty.policy === 'unhealthy'
              ? 'Health Check'
              : null,
          value:
            user.status === 'suspended' &&
            relevantPenalty &&
            relevantPenalty.policy === 'unhealthy'
              ? relevantPenalty.offenses[0].healthCheckAnswers
              : null,
        },
        {
          key: 'Shadow Blocked',
          value: user.shadowBlocked ? 'true' : 'false',
        },
        {
          key: 'Shadow Blocked Reason',
          value: user.shadowBlockedReason,
        },
        {
          key: 'Phone Number',
          value: `${user.phoneNumber || ''}${user.phoneNumberVerifiedAt ? '' : ' (unverified)'}`,
        },
        {
          key: 'Email',
          value: user.email,
        },
        {
          key: 'Address',
          value: user.address
            ? user.address.street +
              ' ' +
              (user.address.unit ? user.address.unit + ' ' : '') +
              user.address.city +
              ' ' +
              user.address.state +
              ' ' +
              user.address.zip
            : 'N/A',
        },
        {
          key: 'Stripe ID',
          value: user.stripeAccountIdProd ? user.stripeAccountIdProd : 'N/A',
          link: `https://dashboard.stripe.com/connect/accounts/${user.stripeAccountIdProd}`,
        },
        {
          key: 'Marqeta ID',
          value: `${user.marqetaAccountId ?? 'N/A'}${user.marqetaEnvironment ? ` (${user.marqetaEnvironment})` : ''}`,
          link:
            user.marqetaEnvironment === 'production'
              ? `https://app.marqeta.com/program/user?program=wclgprew&tab=Transactions&token=${user.marqetaAccountId}`
              : null,
        },
        {
          key: 'Payment Account type',
          value: getPaymentAccountString(user),
        },
        {
          key: 'Orientation Status',
          value: user.orientationStatus,
        },
        {
          key: 'Rating',
          value: user.rating,
        },
        {
          key: 'Account created at',
          value: user.createdAt
            ? moment
                .tz(user.createdAt, 'America/Los_Angeles')
                .format('YYYY-MM-DD hh:mm A')
            : 'N/A',
        },
      ].filter((item) => item.key || item.value?.toString()),
    []
  )

  const missingFields = useMemo(() => {
    const fields: Array<{ field: string; impact: string }> = []
    if (!user.name) {
      fields.push({
        field: 'name',
        impact:
          'User cannot create Stripe account for direct deposit, complete background checks, or be invited as a company worker.',
      })
    }
    if (!user.phoneNumber) {
      fields.push({
        field: 'phone number',
        impact:
          'User cannot verify their number, complete background checks, or receive SMS notifications if enabled.',
      })
    }
    if (!user.email) {
      fields.push({
        field: 'email',
        impact:
          'User cannot complete background checks, add direct deposit information, or receive email notifications.',
      })
    }
    return fields
  }, [user.name, user.phoneNumber, user.email])

  return (
    <CollapsibleSection title="Basic Account Info" defaultOpen>
      {missingFields.length > 0 && (
        <Message variant="warning" mb={3} borderRadius="small">
          <Box>
            <Text fontWeight="medium" mb={2}>
              This user is missing{' '}
              {missingFields.map((f, i) => (
                <React.Fragment key={f.field}>
                  {i > 0 && (i === missingFields.length - 1 ? ' and ' : ', ')}
                  <Text as="span" fontWeight="bold">
                    {f.field}
                  </Text>
                </React.Fragment>
              ))}
              .
            </Text>
            {missingFields.map((field) => (
              <Text key={field.field} fontSize={1} mb={1}>
                • Missing {field.field}: {field.impact}
              </Text>
            ))}
          </Box>
        </Message>
      )}
      <Table variant="borderless" columns={keyValueColumns} data={data} />
      <Accordion
        type="single"
        items={[
          {
            trigger: <Heading level={4}>Profile Picture</Heading>,
            content: (
              <ProfilePicture
                verifiedSelfieUrl={user.verifiedSelfieUrl}
                profilePicUrl={user.profilePicUrl}
                profilePicVerifiedAt={user.profilePicVerifiedAt}
                profilePicManuallyVerifiedBy={user.profilePicManuallyVerifiedBy}
                id={user.id}
                refreshData={refreshData}
              />
            ),
          },
          {
            trigger: <Heading level={4}>Date of Birth</Heading>,
            content: (
              <DateOfBirth
                id={user.id}
                dob={user.dob}
                identityVerificationId={user.identityVerificationId}
                refreshData={refreshData}
              />
            ),
          },
        ]}
      />
      <hr />
      {user.strikes
        ? user.strikes.map((strike) => (
            <UserStrike
              key={strike.id}
              user={user}
              strike={strike}
              refreshData={refreshData}
            />
          ))
        : null}
      {user.allStrikes
        ? user.allStrikes.map((strike) => (
            <UserPastStrike key={strike.id} user={user} strike={strike} />
          ))
        : null}
      <UserPenaltyWaive user={user} refreshData={refreshData} />
    </CollapsibleSection>
  )
}

export default UserBasicInfo
